import {
  CAMPAIGN_GENDERS,
  type CampaignFilters,
  type CampaignGenderKey,
  CUSTOMER_ORIGINS,
  type CustomerOriginKey,
} from '@crm/shared';
import { Checkbox, Input, Label } from '@crm/ui';
import { Autocomplete } from '@/components';
import { useUnits } from '@/modules/company/hooks';

const GENEROS = CAMPAIGN_GENDERS.map((item) => item.key);
const ORIGENS = CUSTOMER_ORIGINS.map((item) => item.key);

/**
 * Lista vazia quer dizer "todos" - e e assim que a tela mostra, com tudo
 * marcado. Em branco parecia formulario por preencher, e marcar uma caixa
 * dava a impressao de somar gente quando o gesto so estreita.
 */
function marcadas<T extends string>(escolhidas: T[], todas: readonly T[]): T[] {
  return escolhidas.length > 0 ? escolhidas : [...todas];
}

/** Remarcar tudo volta ao vazio: guardar a lista inteira diz o mesmo com mais. */
function alterna<T extends string>(escolhidas: T[], todas: readonly T[], valor: T): T[] {
  const atual = marcadas(escolhidas, todas);
  const proxima = atual.includes(valor)
    ? atual.filter((item) => item !== valor)
    : [...atual, valor];

  return proxima.length === todas.length ? [] : proxima;
}

interface AudienceFiltersProps {
  filters: CampaignFilters;
  onChange: (filters: CampaignFilters) => void;
  readOnly?: boolean;
}

/**
 * Os recortes de publico: unidade, idade, sexo e origem.
 *
 * Vive fora do passo da campanha porque a lista de contatos faz a mesma
 * pergunta - e duas telas com os mesmos filtros escritos duas vezes acabam
 * discordando na primeira mudanca.
 */
export function AudienceFilters({ filters, onChange, readOnly }: AudienceFiltersProps) {
  const units = useUnits();
  const unidades = units.data?.data ?? [];

  function idade(campo: 'ageMin' | 'ageMax', valor: string) {
    const numero = Number(valor);
    onChange({ ...filters, [campo]: valor && Number.isFinite(numero) ? numero : null });
  }

  return (
    <div className="space-y-6">
      {/* Quais unidades entram. De qual numero a mensagem sai nao se escolhe:
          e sempre a unidade do proprio cliente. */}
      {unidades.length > 1 ? (
        <Autocomplete
          label="Unidades que participam"
          placeholder="Buscar unidade..."
          emptyLabel="Todas as unidades"
          multiple
          disabled={readOnly}
          options={unidades.map((unit) => ({ id: unit.id, label: unit.name }))}
          value={filters.unitIds.map((id) => ({
            id,
            label: unidades.find((unit) => unit.id === id)?.name ?? id,
          }))}
          onChange={(escolhidas) =>
            onChange({ ...filters, unitIds: escolhidas.map((item) => item.id) })
          }
        />
      ) : null}

      <div className="flex flex-wrap gap-x-12 gap-y-6">
        <div className="space-y-2">
          <Label>Idade</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              placeholder="de"
              className="w-24"
              disabled={readOnly}
              value={filters.ageMin ?? ''}
              onChange={(event) => idade('ageMin', event.target.value)}
            />
            <span className="text-sm text-muted-foreground">ate</span>
            <Input
              type="number"
              inputMode="numeric"
              placeholder="ate"
              className="w-24"
              disabled={readOnly}
              value={filters.ageMax ?? ''}
              onChange={(event) => idade('ageMax', event.target.value)}
            />
          </div>
          <Checkbox
            label="Incluir quem nao informou a idade"
            disabled={readOnly}
            checked={filters.includeUnknownAge}
            onChange={() => onChange({ ...filters, includeUnknownAge: !filters.includeUnknownAge })}
          />
        </div>

        <div className="space-y-2">
          <Label>Sexo</Label>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {CAMPAIGN_GENDERS.map((opcao) => (
              <Checkbox
                key={opcao.key}
                label={opcao.label}
                disabled={readOnly}
                checked={marcadas(filters.gender, GENEROS).includes(opcao.key)}
                onChange={() =>
                  onChange({
                    ...filters,
                    gender: alterna<CampaignGenderKey>(filters.gender, GENEROS, opcao.key),
                  })
                }
              />
            ))}
          </div>
        </div>

        <div className="w-full space-y-2">
          <Label>Origem do cadastro</Label>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {CUSTOMER_ORIGINS.map((opcao) => (
              <Checkbox
                key={opcao.key}
                label={opcao.label}
                disabled={readOnly}
                checked={marcadas(filters.origins, ORIGENS).includes(opcao.key)}
                onChange={() =>
                  onChange({
                    ...filters,
                    origins: alterna<CustomerOriginKey>(filters.origins, ORIGENS, opcao.key),
                  })
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
